import { OrderStatus, type Order } from '@src/domains/orders';

import { isPositiveAmount } from './isPositiveAmount';

export type CashbackGrowthOrderSnapshot = {
  orderId: string;
  code: string;
  amountCents: number;
  buyerFirstName?: string;
};

export const buildCashbackGrowthOrderSnapshot = (
  order: Order,
  buyerFirstName?: string,
): CashbackGrowthOrderSnapshot => ({
  orderId: order.id,
  code: order.code,
  amountCents: order.finalAmountCurrencyInfo.amountCents,
  buyerFirstName,
});

/** Most recent purchase in the buyer order list (API returns newest first). */
export const findLatestOrder = (orders: Order[] | undefined): Order | null => {
  if (!orders?.length) {
    return null;
  }

  return orders[0] ?? null;
};

export const isCashbackGrowthEligibleOrder = (order: Order): boolean =>
  order.status === OrderStatus.Confirmed && isPositiveAmount(order.finalAmountCurrencyInfo.amountCents);

export const findOrderMatchingSnapshot = (
  orders: Order[] | undefined,
  snapshot: CashbackGrowthOrderSnapshot | null,
): Order | null => {
  if (!snapshot || !orders?.length || !isPositiveAmount(snapshot.amountCents)) {
    return null;
  }

  return (
    orders.find(
      (order) =>
        order.id === snapshot.orderId &&
        order.code === snapshot.code &&
        order.finalAmountCurrencyInfo.amountCents === snapshot.amountCents,
    ) ?? null
  );
};

export const parseCashbackGrowthOrderSnapshot = (raw: string | null): CashbackGrowthOrderSnapshot | null => {
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CashbackGrowthOrderSnapshot;
    if (
      typeof parsed.orderId === 'string' &&
      typeof parsed.code === 'string' &&
      typeof parsed.amountCents === 'number'
    ) {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
};
